import { useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import { Icon } from '@antimatterhq/ui/components/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@antimatterhq/ui/components/dropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@antimatterhq/ui/components/dialog';
import { formatDistanceToNow } from 'date-fns';
import { recentQueriesAtom, openQueryTabAtom } from '@/atoms/query/queryAtoms';

export function QueryRecentsList() {
  const [recentQueries, setRecentQueries] = useAtom(recentQueriesAtom);
  const openTab = useSetAtom(openQueryTabAtom);
  const [deleteQueryId, setDeleteQueryId] = useState<string | null>(null);

  const handleDeleteQuery = (queryId: string) => {
    setRecentQueries(recentQueries.filter((q) => q.id !== queryId));
    setDeleteQueryId(null);
  };

  if (recentQueries.length === 0) {
    return (
      <div className="py-8 text-center" data-testid="recent-queries-empty">
        <Text className="text-sm text-gray-500">No recent queries</Text>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2" data-testid="recent-queries-list">
        {recentQueries.map((query) => (
          <div
            key={query.id}
            className="flex items-center justify-between rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-900"
            data-testid={`recent-query-${query.id}`}
          >
            <button
              onClick={() => openTab(query.id)}
              className="flex flex-1 flex-col items-start text-left"
              data-testid={`recent-query-open-${query.id}`}
            >
              <Text className="font-medium">{query.name}</Text>
              <Text className="text-xs text-gray-500">
                Modified {formatDistanceToNow(new Date(query.lastModified))} ago
              </Text>
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="small"
                  iconOnly
                  leftIcon="radix-icons:dots-horizontal"
                  data-testid={`recent-query-menu-${query.id}`}
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setDeleteQueryId(query.id)}
                  className="text-red-600 dark:text-red-400"
                  data-testid={`recent-query-delete-${query.id}`}
                >
                  <Icon icon="antimatterhq:trash" className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      <Dialog
        open={deleteQueryId !== null}
        onOpenChange={(open) => !open && setDeleteQueryId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Query</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this query? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="tertiary" onClick={() => setDeleteQueryId(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteQueryId && handleDeleteQuery(deleteQueryId)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
